/**
 * Classificação de porte da empresa - mesma regra do back (App/Helpers/Porte.ts).
 *
 * Só por faturamento, nunca por número de funcionários (pedido do negócio em
 * 2026-09-18):
 *   1. a faixa da Receita Federal (LC 123/2006): Microempresa, Pequeno Porte ou
 *      "Demais" (acima de 4,8 milhões por ano);
 *   2. o "Demais" é separado entre média e grande pelo faturamento informado (corte
 *      do BNDES em 300 milhões) - sem faturamento informado, fica média;
 *   3. sem classificação da Receita, vale o faturamento informado nas mesmas faixas;
 *      sem nenhum dos dois, fica sem porte.
 */

export const PORTE = {
  MICRO: "Microempresa",
  PEQUENA: "Pequena Empresa",
  MEDIA: "Média Empresa",
  GRANDE: "Grande Empresa",
};

/** codigo_porte da Receita Federal, como vem da consulta de CNPJ. */
export const CODIGO_PORTE_RECEITA = {
  NAO_INFORMADO: 0,
  MICRO: 1,
  PEQUENO: 3,
  DEMAIS: 5,
};

/** Faixas de receita bruta anual: LC 123/2006 (micro e pequena) e BNDES (média e grande). */
export const LIMITE_FATURAMENTO_MICRO = 360000;
export const LIMITE_FATURAMENTO_PEQUENA = 4800000;
export const LIMITE_FATURAMENTO_MEDIA = 300000000;

const faturamentoValido = (valor) => {
  if (valor === null || valor === undefined || valor === "") return null;
  const numero = Number(valor);
  return Number.isFinite(numero) && numero > 0 ? numero : null;
};

export const portePorFaturamento = (valor) => {
  const faturamento = faturamentoValido(valor);
  if (faturamento === null) return null;
  if (faturamento <= LIMITE_FATURAMENTO_MICRO) return PORTE.MICRO;
  if (faturamento <= LIMITE_FATURAMENTO_PEQUENA) return PORTE.PEQUENA;
  if (faturamento <= LIMITE_FATURAMENTO_MEDIA) return PORTE.MEDIA;
  return PORTE.GRANDE;
};

const temClassificacaoDaReceita = (codigo) =>
  [CODIGO_PORTE_RECEITA.MICRO, CODIGO_PORTE_RECEITA.PEQUENO, CODIGO_PORTE_RECEITA.DEMAIS].includes(
    Number(codigo)
  );

/** Porte: Receita primeiro, faturamento informado depois, senão sem porte. */
export const classificarPorte = ({ codigoPorteReceita, valorFaturamento }) => {
  switch (Number(codigoPorteReceita)) {
    case CODIGO_PORTE_RECEITA.MICRO:
      return PORTE.MICRO;
    case CODIGO_PORTE_RECEITA.PEQUENO:
      return PORTE.PEQUENA;
    case CODIGO_PORTE_RECEITA.DEMAIS: {
      const faturamento = faturamentoValido(valorFaturamento);
      return faturamento !== null && faturamento > LIMITE_FATURAMENTO_MEDIA
        ? PORTE.GRANDE
        : PORTE.MEDIA;
    }
    default:
      return portePorFaturamento(valorFaturamento);
  }
};

/** De onde saiu a classificação, pra explicar na tela. */
export const origemDoPorte = ({ codigoPorteReceita, valorFaturamento }) => {
  const temFaturamento = faturamentoValido(valorFaturamento) !== null;

  if (!temClassificacaoDaReceita(codigoPorteReceita)) {
    return temFaturamento
      ? "Classificado pelo faturamento informado (sem classificação da Receita Federal)."
      : "Sem classificação: informe o faturamento anual para definir o porte.";
  }
  if (Number(codigoPorteReceita) !== CODIGO_PORTE_RECEITA.DEMAIS) {
    return "Classificado pela faixa de faturamento da Receita Federal.";
  }
  return temFaturamento
    ? "Classificado pela Receita Federal (faixa “Demais”) e pelo faturamento informado."
    : "Classificado pela Receita Federal (faixa “Demais”). Informe o faturamento para diferenciar média de grande empresa.";
};
